import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { isUserAuthorized } from '../../util/userHelpers.js';
import {
  Heading,
  Page,
  LayoutSingleColumn,
  PrimaryButton,
  SecondaryButton,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  listSubscriptionTiers,
  createSubscriptionCheckout,
  createSubscriptionPortal,
  getCurrentSubscription,
} from '../../util/api';

import css from './PricingPage.module.css';

const formatPrice = (intl, price) => {
  if (price?.unitAmount == null) return '';
  return intl.formatNumber(price.unitAmount / 100, {
    style: 'currency',
    currency: (price.currency || 'usd').toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const intervalLabel = (intl, price) => {
  if (!price?.interval) return '';
  return intl.formatMessage(
    { id: `PricingPage.interval.${price.interval}` },
    { count: price.intervalCount || 1 }
  );
};

const featureKeysFromIntl = (intl, tierId) => {
  const messages = intl?.messages || {};
  const keys = [];
  for (let i = 1; ; i++) {
    const key = `PricingPage.${tierId}.feature.${i}`;
    const value = messages[key];
    if (typeof value === 'string' && value) {
      keys.push(key);
    } else {
      break;
    }
  }
  return keys;
};

const SUPPORTED_INTERVALS = ['month', 'year'];

const readIntervalFromLocation = () => {
  if (typeof window === 'undefined') return 'year';
  const value = new URLSearchParams(window.location.search).get('interval');
  return SUPPORTED_INTERVALS.includes(value) ? value : 'year';
};

const writeIntervalToLocation = interval => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  params.set('interval', interval);
  const next = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', next);
};

const TierCard = ({ tier, intl, billingInterval, isAuthenticated, isApproved, busy, onSubscribe }) => {
  const price =
    tier.prices.find(p => p.interval === billingInterval) ||
    tier.prices.find(p => p.id === tier.defaultPriceId) ||
    tier.prices[0];

  const name = tier.productName || tier.tier;
  const description = tier.productDescription || '';

  const handleClick = () => {
    if (!isAuthenticated) {
      window.location.href = '/signup/vendor?from=/pricing';
      return;
    }
    if (price) onSubscribe(price.id);
  };

  return (
    <article className={css.tierCard}>
      <Heading as="h2" rootClassName={css.tierName}>
        {name}
      </Heading>
      {price ? (
        <p className={css.tierPrice}>
          <span className={css.tierAmount}>{formatPrice(intl, price)}</span>
          <span className={css.tierInterval}>{intervalLabel(intl, price)}</span>
        </p>
      ) : null}
      {description ? <p className={css.tierDescription}>{description}</p> : null}
      <ul className={css.tierFeatures}>
        {featureKeysFromIntl(intl, tier.tier).map(key => (
          <li key={key} className={css.tierFeature}>
            <FormattedMessage id={key} />
          </li>
        ))}
      </ul>
      <PrimaryButton
        className={css.subscribeCta}
        disabled={!price || busy || (isAuthenticated && !isApproved)}
        onClick={handleClick}
      >
        {!isAuthenticated ? (
          <FormattedMessage id="PricingPage.signupToSubscribeCta" />
        ) : !isApproved ? (
          <FormattedMessage id="PricingPage.pendingApprovalCta" />
        ) : (
          <FormattedMessage id="PricingPage.subscribeCta" />
        )}
      </PrimaryButton>
    </article>
  );
};

export const PricingPageComponent = props => {
  const { scrollingDisabled, isAuthenticated, currentUser } = props;
  const intl = useIntl();
  const isApproved = isUserAuthorized(currentUser);

  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [billingInterval, setBillingIntervalState] = useState(readIntervalFromLocation);

  const setBillingInterval = next => {
    setBillingIntervalState(next);
    writeIntervalToLocation(next);
  };
  const [busy, setBusy] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listSubscriptionTiers()
      .then(response => {
        if (!cancelled) {
          setTiers(response?.tiers || []);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setLoadError(err);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setHasSubscription(false);
      return undefined;
    }
    let cancelled = false;
    getCurrentSubscription()
      .then(response => {
        if (!cancelled) setHasSubscription(!!response?.subscription);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleSubscribe = priceId => {
    setBusy(true);
    createSubscriptionCheckout(priceId)
      .then(({ url }) => {
        if (url) window.location.href = url;
      })
      .catch(() => setBusy(false));
  };

  const handleManage = () => {
    setBusy(true);
    createSubscriptionPortal()
      .then(({ url }) => {
        if (url) window.location.href = url;
      })
      .catch(() => setBusy(false));
  };

  const title = intl.formatMessage({ id: 'PricingPage.title' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        mainColumnClassName={css.layoutMain}
        topbar={<TopbarContainer currentPage="PricingPage" />}
        footer={<FooterContainer />}
      >
        <section className={css.root}>
          <header className={css.header}>
            <Heading as="h1" rootClassName={css.heading}>
              <FormattedMessage id="PricingPage.title" />
            </Heading>
            <p className={css.subtitle}>
              <FormattedMessage id="PricingPage.subtitle" />
            </p>
          </header>

          {loading ? (
            <p className={css.status}>
              <FormattedMessage id="PricingPage.loading" />
            </p>
          ) : loadError ? (
            <p className={css.statusError}>
              <FormattedMessage id="PricingPage.loadError" />
            </p>
          ) : tiers.length === 0 ? (
            <p className={css.status}>
              <FormattedMessage id="PricingPage.noTiers" />
            </p>
          ) : (
            <>
              <div
                className={css.intervalToggle}
                role="group"
                aria-label={intl.formatMessage({ id: 'PricingPage.intervalToggle.ariaLabel' })}
              >
                <button
                  type="button"
                  aria-pressed={billingInterval === 'month'}
                  className={billingInterval === 'month' ? css.intervalActive : css.intervalOption}
                  onClick={() => setBillingInterval('month')}
                >
                  <FormattedMessage id="PricingPage.intervalToggle.monthly" />
                </button>
                <button
                  type="button"
                  aria-pressed={billingInterval === 'year'}
                  className={billingInterval === 'year' ? css.intervalActive : css.intervalOption}
                  onClick={() => setBillingInterval('year')}
                >
                  <FormattedMessage id="PricingPage.intervalToggle.yearly" />
                </button>
              </div>
              <div className={css.tiers}>
                {tiers.map(tier => (
                  <TierCard
                    key={tier.tier}
                    tier={tier}
                    intl={intl}
                    billingInterval={billingInterval}
                    isAuthenticated={isAuthenticated}
                    isApproved={isApproved}
                    busy={busy}
                    onSubscribe={handleSubscribe}
                  />
                ))}
              </div>
            </>
          )}

          {isAuthenticated && hasSubscription ? (
            <div className={css.manage}>
              <SecondaryButton onClick={handleManage} disabled={busy}>
                <FormattedMessage id="PricingPage.manageCta" />
              </SecondaryButton>
            </div>
          ) : null}
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  isAuthenticated: state.auth.isAuthenticated,
  currentUser: state.user.currentUser,
});

const PricingPage = compose(connect(mapStateToProps))(PricingPageComponent);

export default PricingPage;
